import { Link } from "react-router-dom";
import { DataTable, PersonCell, Pill, StatusPill, type Column } from "@guestpilot/ui-kit";
import type { Team } from "../api/types";
import { formatDate, formatDateTime } from "../format";
import { useI18n } from "../i18n";

/** Most severe first: danger risks, then warnings (04 §6). */
export const RISK_ORDER = [
  "expired",
  "overdue_review",
  "unknown_domain",
  "removal_requested",
  "no_sponsor",
  "no_expiry",
  "no_reason",
  "expiring_soon",
] as const;

export function worstRisk(
  counts: Record<string, number> | undefined,
): { type: string; count: number } | null {
  for (const r of RISK_ORDER) if ((counts?.[r] ?? 0) > 0) return { type: r, count: counts![r]! };
  return null;
}

export function RiskPill({ counts }: { counts: Record<string, number> | undefined }) {
  const { t } = useI18n();
  const w = worstRisk(counts);
  if (!w) return <Pill variant="success">{t("ui:risk.clear")}</Pill>;
  return <StatusPill domain="risk" value={w.type} label={`${w.count} · ${t(`ui:risk.${w.type}`)}`} />;
}

export function TeamsTable({
  rows,
  loading,
  error,
  onRetry,
  emptyAction,
  tz,
}: {
  rows: Team[] | undefined;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  emptyAction?: React.ReactNode;
  tz?: string | undefined;
}) {
  const { t, locale } = useI18n();
  const columns: Column<Team>[] = [
    {
      key: "name",
      header: t("ui:teams.col.team"),
      render: (r) => (
        <div className="gp-person-text">
          <Link to={`/teams/${r.id}`} className="t-body-strong">
            {r.displayName}
          </Link>
          <span className="gp-person-sub t-body-sm">
            {t("ui:teams.activatedOn", { date: formatDate(r.activatedAt, locale, tz) })}
          </span>
        </div>
      ),
    },
    {
      key: "owner",
      header: t("ui:teams.col.owner"),
      render: (r) =>
        (r.owners ?? []).length ? (
          <div className="gp-stack" style={{ gap: "var(--space-4)" }}>
            {(r.owners ?? []).map((o) => (
              <PersonCell key={o.id} name={o.displayName} avatar />
            ))}
          </div>
        ) : (
          <Pill variant="warn">{t("ui:teams.ownerless")}</Pill>
        ),
    },
    { key: "users", header: t("ui:teams.col.users"), numeric: true, render: (r) => r.externalUserCount ?? 0 },
    { key: "risk", header: t("ui:teams.col.risk"), render: (r) => <RiskPill counts={r.riskCounts} /> },
    {
      key: "scan",
      header: t("ui:teams.col.scan"),
      render: (r) => <span className="t-body-sm gp-muted">{formatDateTime(r.lastScanAt, locale, tz)}</span>,
    },
    {
      key: "review",
      header: t("ui:teams.col.review"),
      render: (r) =>
        r.status === "active" ? (
          <StatusPill
            domain="campaign"
            value={
              r.reviewStatus === "completed"
                ? "completed"
                : r.reviewStatus === "in_progress"
                  ? "in_progress"
                  : "cancelled"
            }
            label={t(`ui:review.${r.reviewStatus ?? "not_started"}`)}
          />
        ) : (
          <StatusPill domain="team" value={r.status} label={t(`ui:teamStatus.${r.status}`)} />
        ),
    },
    {
      key: "view",
      header: t("ui:table.actions"),
      render: (r) => (
        <Link to={`/teams/${r.id}`} className="t-body-strong">
          {t("ui:teams.view")}
        </Link>
      ),
    },
  ];
  return (
    <DataTable
      caption={t("ui:teams.caption")}
      columns={columns}
      rows={rows}
      loading={loading}
      error={error}
      onRetry={onRetry}
      errorTitle={t("ui:table.error")}
      retryLabel={t("common:retry")}
      rowKey={(r) => r.id}
      emptyTitle={t("ui:teams.empty")}
      emptyAction={emptyAction}
    />
  );
}
